import React, { useState, useEffect } from 'react';
import { Client, ClientContact } from '../../../entities/client';
import { updateClient } from '../../../entities/client/api/clientApi';
import * as S from './EditClientModal.styles';

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
  onClientUpdated: (client: Client) => void;
}

const getInitialContacts = (client: Client): ClientContact[] => {
  const contacts = client.contacts || [];
  if (contacts.length > 0) {
    return contacts;
  }
  if (client.mainContact) {
    return [client.mainContact];
  }
  return [];
};

export const EditClientModal: React.FC<EditClientModalProps> = ({
  isOpen,
  onClose,
  client,
  onClientUpdated,
}) => {
  const [name, setName] = useState(client.name);
  const [type, setType] = useState(client.type);
  const [uid, setUid] = useState(client.uid || '');
  const [region, setRegion] = useState(client.region || '');
  const [contacts, setContacts] = useState<ClientContact[]>(
    getInitialContacts(client)
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(client.name);
      setType(client.type);
      setUid(client.uid || '');
      setRegion(client.region || '');
      setContacts(getInitialContacts(client));
    }
  }, [isOpen, client]);

  const handleContactChange = (index: number, field: keyof ClientContact, value: string | boolean) => {
    const updatedContacts = [...contacts];
    updatedContacts[index] = { ...updatedContacts[index], [field]: value };
    setContacts(updatedContacts);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      alert('Введите название клиента');
      return;
    }

    if (contacts.length === 0) {
      alert('Добавьте хотя бы один контакт');
      return;
    }

    try {
      setSaving(true);
      
      if (contacts.length === 0) {
        alert('Добавьте хотя бы один контакт');
        return;
      }

      const validContacts = contacts.filter(c => c.email && c.email.trim());
      if (validContacts.length === 0) {
        alert('Добавьте хотя бы один контакт с email');
        return;
      }

      const mainContactId = client.mainContact?.id || validContacts[0]?.id;
      const contactsToSend = validContacts.map((c, index) => ({
        id: c.id || `temp-${index}`,
        email: c.email.trim(),
        name: c.name?.trim() || undefined,
        phone: c.phone?.trim() || undefined,
        is_main: c.id === mainContactId || (index === 0 && !mainContactId),
      }));
      
      const updatedClient = await updateClient(client.id, {
        name: name.trim(),
        type,
        uid: uid.trim() || undefined,
        region: region.trim() || undefined,
        contacts: contactsToSend,
      });
      onClientUpdated(updatedClient);
      handleClose();
    } catch (error) {
      console.error('Ошибка обновления клиента:', error);
      alert('Не удалось обновить клиента');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setName(client.name);
    setType(client.type);
    setUid(client.uid || '');
    setRegion(client.region || '');
    setContacts(client.contacts || []);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <S.ModalOverlay onClick={handleClose}>
      <S.ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <S.ModalHeader>
          <S.ModalTitle>Редактировать клиента</S.ModalTitle>
          <S.CloseButton onClick={handleClose}>×</S.CloseButton>
        </S.ModalHeader>

        <form onSubmit={handleSubmit}>
          <S.ModalBody>
            <S.FormGroup>
              <S.Label>Название *</S.Label>
              <S.Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </S.FormGroup>

            <S.FormGroup>
              <S.Label>Тип</S.Label>
              <S.Select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="individual">Физическое лицо</option>
                <option value="company">Компания</option>
                <option value="organization">Организация</option>
              </S.Select>
            </S.FormGroup>

            <S.FormGroup>
              <S.Label>UID</S.Label>
              <S.Input
                type="text"
                value={uid}
                onChange={(e) => setUid(e.target.value)}
              />
            </S.FormGroup>

            <S.FormGroup>
              <S.Label>Регион</S.Label>
              <S.Input
                type="text"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              />
            </S.FormGroup>

            <S.SectionTitle>Контакты</S.SectionTitle>
            {contacts.length === 0 ? (
              <S.EmptyContactsMessage>
                Нет контактов. Добавьте контакт с email.
              </S.EmptyContactsMessage>
            ) : (
              contacts.map((contact, index) => (
              <S.ContactCard key={contact.id || index}>
                <S.ContactHeader>
                  <S.ContactTitle>Контакт {index + 1}</S.ContactTitle>
                  {(contact.id === client.mainContact?.id || contact.is_main) && <S.MainBadge>Основной</S.MainBadge>}
                </S.ContactHeader>
                <S.FormGroup>
                  <S.Label>Имя</S.Label>
                  <S.Input
                    type="text"
                    value={contact.name || ''}
                    onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <S.Label>Email *</S.Label>
                  <S.Input
                    type="email"
                    value={contact.email}
                    onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                    required
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <S.Label>Телефон</S.Label>
                  <S.Input
                    type="tel"
                    value={contact.phone || ''}
                    onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                  />
                </S.FormGroup>
              </S.ContactCard>
              ))
            )}
          </S.ModalBody>

          <S.ModalFooter>
            <S.Button type="button" onClick={handleClose}>
              Отмена
            </S.Button>
            <S.Button type="submit" $variant="primary" disabled={saving}>
              {saving ? 'Сохранение...' : 'Сохранить'}
            </S.Button>
          </S.ModalFooter>
        </form>
      </S.ModalContent>
    </S.ModalOverlay>
  );
};

